import React, { useState } from "react";
import { Search, SlidersHorizontal, Calendar, ChevronDown } from "lucide-react";
import { LIST_STATUS_FILTERS } from "../../../constants/tspJourneyStatus";
import { DEFAULT_JOURNEY_FILTERS } from "../useTspJourneysListData";

// Filter bar. The selects stay native <select>s (keyboard / screen-reader /
// mobile-picker friendly); the wrapper only supplies the design's thin chevron
// in place of the browser arrow. Changes are held as a draft and only sent to
// the list when Apply Filters is pressed (or Enter in the search box).
function FilterSelect({ label, value, onChange, children }) {
  return (
    <span className="tsp-filter-select-wrap">
      <select className="tsp-select tsp-filter-select" value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
        {children}
      </select>
      <ChevronDown size={16} aria-hidden="true" />
    </span>
  );
}

export default function JourneyFilters({ applied, options, onApply }) {
  const [draft, setDraft] = useState(applied);
  const [showDates, setShowDates] = useState(Boolean(applied.from || applied.to));

  const set = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }));
  // The year never changes the chosen status: Completed + 2026 shows only the
  // completed journeys of 2026 (the status default is already All Journeys).
  const setYear = set("year");
  const badRange = Boolean(draft.from && draft.to && draft.to < draft.from);
  const hasDates = Boolean(draft.from || draft.to);
  // A year from the URL that no journey has (yet) stays selectable, so the select shows what is applied.
  const yearOptions = [...new Set([...(options.years || []), ...(draft.year ? [Number(draft.year)] : [])])].sort((a, b) => b - a);

  const submit = (event) => {
    event.preventDefault();
    if (!badRange) onApply(draft);
  };

  const reset = () => {
    setDraft(DEFAULT_JOURNEY_FILTERS);
    setShowDates(false);
    onApply(DEFAULT_JOURNEY_FILTERS);
  };

  return (
    <form className="tsp-journey-filters" onSubmit={submit}>
      <label className="tsp-journey-search">
        <Search size={16} />
        <input type="text" placeholder="Search journeys..." aria-label="Search journeys" value={draft.q} onChange={(event) => set("q")(event.target.value)} />
      </label>
      <FilterSelect label="Journey status filter" value={draft.status} onChange={set("status")}>
        {Object.entries(LIST_STATUS_FILTERS).map(([key, filter]) => (
          <option key={key} value={key}>
            {filter.label}
          </option>
        ))}
      </FilterSelect>
      {/* Year-wise journeys: the years of real journeys (server-derived from start dates), newest first. */}
      <FilterSelect label="Year filter" value={draft.year || ""} onChange={setYear}>
        <option value="">All Years</option>
        {yearOptions.map((year) => (
          <option key={year} value={String(year)}>
            {year}
          </option>
        ))}
      </FilterSelect>
      {options.temples && options.temples.length > 0 && (
        <FilterSelect label="Temple filter" value={draft.temple || ""} onChange={set("temple")}>
          <option value="">Temple</option>
          {options.temples.map((temple) => (
            <option key={temple} value={temple}>
              {temple}
            </option>
          ))}
        </FilterSelect>
      )}
      <FilterSelect label="State filter" value={draft.stateId} onChange={set("stateId")}>
        <option value="">State</option>
        {options.states.map((state) => (
          <option key={state.id} value={state.id}>
            {state.name}
          </option>
        ))}
      </FilterSelect>
      <button type="button" className="tsp-select tsp-date-range-btn" aria-expanded={showDates} onClick={() => setShowDates((open) => !open)}>
        <Calendar size={14} /> Date Range{hasDates ? " •" : ""}
      </button>
      <button type="submit" className="tsp-btn tsp-btn-primary tsp-btn-lg tsp-apply-filters-btn" disabled={badRange}>
        <SlidersHorizontal size={14} /> Apply Filters
      </button>
      <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-lg" onClick={reset}>
        Reset
      </button>

      {showDates && (
        <div className="tsp-journey-date-range">
          <label>
            From
            <input type="date" className="tsp-input" aria-label="From date" value={draft.from} max={draft.to || undefined} onChange={(event) => set("from")(event.target.value)} />
          </label>
          <label>
            To
            <input type="date" className="tsp-input" aria-label="To date" value={draft.to} min={draft.from || undefined} onChange={(event) => set("to")(event.target.value)} />
          </label>
          {badRange && (
            <span className="tsp-form-error" role="alert">
              The end date cannot be before the start date.
            </span>
          )}
        </div>
      )}
    </form>
  );
}
